import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Asterisk } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Preloader({ onDone }) {
  const { t } = useLanguage();
  const reduced = useReducedMotion();
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    const started = performance.now();
    const duration = reduced ? 100 : 1750;
    const interval = window.setInterval(() => {
      const value = Math.min(100, Math.round((performance.now() - started) / duration * 100));
      setProgress(value);
      if (value === 100) setVisible(false);
    }, 30);
    return () => {
      clearInterval(interval);
      document.body.style.overflow = previousOverflow;
      window.__lenis?.start();
    };
  }, [reduced]);

  return (
    <AnimatePresence onExitComplete={onDone}>
      {visible && (
        <motion.div data-testid="preloader" className="intro" role="dialog" aria-modal="true" aria-label={t("refresh.intro")}
          exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 0.2, delay: reduced ? 0 : 0.4 }}>
          <div className="intro-curtains" aria-hidden="true">
            {[0, 1, 2, 3, 4].map(i => <motion.div key={i} exit={{ y: "-105%" }} transition={{ duration: 0.5, delay: i * 0.035, ease: [0.76, 0, 0.24, 1] }} />)}
          </div>
          <motion.div className="intro-cinematic-art" aria-hidden="true" initial={{ clipPath: reduced ? "inset(0%)" : "inset(0% 0% 100% 0%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }} exit={{ clipPath: "inset(0% 0% 100% 0%)" }} transition={{ duration: reduced ? 0 : 0.75, ease: [0.76, 0, 0.24, 1] }}><motion.img src="/images/studio-cinematic.webp" alt="" width="2400" height="1100" fetchPriority="high" initial={{ scale: reduced ? 1 : 1.18 }} animate={{ scale: 1 }} transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }} /><div /></motion.div>
          <div className="intro-top"><span className="mono-label"><i className="intro-live-mark" />WB / INDEPENDENT STUDIO</span><span className="mono-label">{t("refresh.location")}</span></div>
          <motion.div className="intro-blueprint" aria-hidden="true" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 0.9 }}><span /><span /><span /></motion.div>
          <motion.div className="intro-center" exit={{ opacity: 0, y: -25 }} transition={{ duration: 0.25 }}>
            <div className="intro-mark" aria-hidden="true">
              <div className="intro-registration intro-registration-a" /><div className="intro-registration intro-registration-b" />
              <svg viewBox="0 0 240 160" fill="none">
                <motion.path d="M20 34L43 126L72 64L101 126L124 34" stroke="#f04238" strokeWidth="13" strokeLinejoin="miter"
                  initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: reduced ? 0 : 1, ease: "easeInOut" }} />
                <motion.path d="M144 126V34H182C220 34 220 79 182 79H144H185C226 79 226 126 185 126H144" stroke="#e2c16c" strokeWidth="12"
                  initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: reduced ? 0 : 1.1, delay: reduced ? 0 : 0.2, ease: "easeInOut" }} />
              </svg>
              <motion.div className="intro-scan" initial={{ top: "0%" }} animate={{ top: "100%" }} transition={{ duration: 1.5, ease: "easeInOut" }} />
            </div>
            <p className="intro-wordmark" aria-label="Web Builders">{["WEB", "BUILDERS"].map((word, i) => <span className="intro-word-mask" key={word}><motion.span aria-hidden="true" initial={{ y: "110%", rotate: 4 }} animate={{ y: 0, rotate: 0 }} transition={{ duration: reduced ? 0 : 0.85, delay: reduced ? 0 : 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}>{word}</motion.span></span>)}</p>
            <p className="mono-label intro-caption" data-testid="intro-caption">{t("refresh.intro")}</p>
            <div className="intro-stages" aria-hidden="true">{t("refresh.introStages").map((stage, i) => <span key={i} className={progress >= i * 33 ? "active" : ""}><small>0{i + 1}</small>{stage}</span>)}</div>
          </motion.div>
          <motion.div className="intro-asterisk" aria-hidden="true" animate={{ rotate: reduced ? 0 : 180 }} transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}><Asterisk size={70} strokeWidth={0.7} /></motion.div>
          <div className="intro-bottom">
            <span data-testid="preloader-counter" className="intro-count" aria-hidden="true">{String(progress).padStart(3, "0")}<small> / 100</small></span>
            <button data-testid="intro-skip-button" className="text-link" onClick={() => setVisible(false)} autoFocus>{t("refresh.skip")}<ArrowUpRight size={16} /></button>
          </div>
          <div className="intro-frame-corners" aria-hidden="true"><span /><span /><span /><span /></div>
          <div className="intro-progress" aria-hidden="true"><div style={{ transform: `scaleX(${progress / 100})` }} /></div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}